/**
 * **条码 / 二维码编码** —— 打印域里唯一一处调用编码器的地方。
 *
 * ```text
 *   符号 + 文本 ──encode()──► 模块几何（1D：条的 x/w；2D：模块矩阵）
 *                                   │
 *                        布局时再乘「整数点模块宽」（units.ts 的 micronToDots）
 *                                   ▼
 *                            点几何 ──► IR / 预览
 * ```
 *
 * ## ★ 三个决定，都写在前面（这一块最容易做成"看起来对但扫不出来"）
 *
 * ### ① 用 `bwip-js/generic` + **命名导入**，而不是 `raw()`
 *
 * `print-template-architecture.md` §5.3 写了两条规矩，实测**不可能同时成立**：
 *
 * | 写法 | 结果 |
 * |---|---|
 * | `import bwipjs from 'bwip-js/generic'` + `bwipjs.raw({bcid,text})` | 拿到权威的 `sbs`/`pixs`（模块单位），**但整包 960 KB**（111 个编码器全在） |
 * | `import { code128, qrcode } from 'bwip-js/generic'` | 可按需裁剪（5 个符号 ≈ 225 KB min / 75 KB gzip），**但泛型入口的具名 `raw` 是 BWIPP 的 raw 伪符号**（它要的"文本"是一串 1-9 的数字），不是"给我原始几何" |
 *
 * 也就是说「命名导入裁剪」与「只用 `raw()`」二者只能取一。这里取**命名导入**，
 * 代价是几何要从 bwip 的**绘图调用流**里收（下面 `collect()`），
 * 而收益是 bundle 小 4 倍、且加一个符号只多一行 import。
 *
 * ⚠️ 这条**不是**"用了它的渲染器"：我们只收 `line` / `polygon` 两类调用
 * （纯矢量、无像素），并且**强制 `scale` 与模块对齐**（见 ②），
 * 最终的整数点吸附仍然由我们做（`units.ts` 那一个换算）。
 * 而且 `verify:print` 会拿**权威 `raw()`**（只在验证脚本里 import，
 * 不进产物）逐条对拍：条的宽度序列 == `sbs`、矩阵 == `pixs`。
 * 对不上就红 —— 这条对拍是这次改写的**唯一**底气。
 *
 * ### ② `scale` 必须是偶数，而且用它换算回模块
 *
 * bwip 的绘图坐标是**像素对齐**的：`line(x0,…,lw)` 里 `x0` 是**描边中心**，
 * 而且是向上取整过的（实测：模块宽 1、scale 1 时 x0 = 左边缘 + 1）。
 * 所以模块宽 = `ceil(lw / 2)` 反推左边缘会带上取整误差。
 * 传一个**偶数 scale**（这里 8）之后 `lw` 必为偶数 → 左边缘 = `x0 - lw/2`
 * 精确可逆，误差为零。
 *
 * ⚠️ 模块在绘图单位里的值取**最后一次 `scale()` 调用**（bwip 会为 2D
 * 追加一次：QR 在 scale=1 时单位模块 = 2，scale=2 时 = 4）——
 * 不能假设"1 模块 = scale"，也不能用 gcd 去猜。
 *
 * ### ③ 静区是**我们的**，不是 bwip 的
 *
 * 实测：`qz` 选项对 code128 无效，`init()` 报的宽度**不含静区**，
 * 绘制的条也从 x=0 开始。而静区是规范要求（Code 128 / EAN 要 10 模块、
 * QR 要 4 模块）—— 少了它**扫码率直接掉**，而且它是"符号占地"的一部分。
 * 所以静区由我们按符号规范加，并算进 `modules`（占地宽度）。
 */
import { code128, code39, ean13, qrcode, datamatrix } from 'bwip-js/generic'
import { GS1_MIN_X_MM, MM_PER_INCH, micronToDots, minGs1ModuleDots } from './units'

/**
 * 传给 bwip 的缩放：**必须偶数**（见文件头 ②）。
 *
 * 8 足够大：模块 8 个单位时，绘图坐标的取整误差（≤0.5 单位）占模块的 6%，
 * 但因为我们走的是"中心 ± 半宽"的精确可逆公式，误差实际为 0。
 */
const ENCODER_SCALE = 8

/* ══════════════════════════════════════════════════════════════════
   符号表
   ══════════════════════════════════════════════════════════════════ */

/**
 * 编码选项（现在只有纠错等级）。
 *
 * ⚠️ 参数化编码 = **缓存键要多一位**：同一个内容在不同纠错等级下
 * 矩阵完全不同（21×21 vs 25×25），键里少了 `ecLevel` 就会出现
 * "改了等级、纸上没变" —— 而且换个内容又对了，最难查的那种。
 */
export interface EncodeOptions {
  /** 二维码的纠错等级（L / M / Q / H）；符号不支持时忽略 */
  ecLevel?: string
}

export interface SymbologySpec {
  /** bwip 的 `bcid`（也是节点上枚举控件的值） */
  id: string
  label: string
  kind: '1d' | '2d'
  /** 规范要求的最小静区（模块）—— Code 128 / EAN 是 10，QR 是 4，Data Matrix 是 1 */
  quietZone: number
  hint: string
  /** 新建时的示例内容（**能通过校验**的那种，别让新节点一放下来就报错） */
  sample: string
  /**
   * 支持纠错等级（QR 有四档；Data Matrix 的等级由符号尺寸推、不许显式指定）。
   *
   * ⚠️ 写在符号表里而不是"界面上一律给个下拉"：给 Data Matrix 传 `eclevel`
   * 会被编码器拒绝 —— 那种"选了就报错"的控件比没有更坏。
   */
  ecLevels?: readonly string[]
}

/**
 * **支持的符号**（白名单）。
 *
 * ⚠️ 这是一个**闭集**：每多一项就多一份编码器进 bundle（≈ 15–40 KB min），
 * 所以"支持多少种"是个产品决定，不是顺手加的。加一项的代价 = 一行 import +
 * 一行表项 + `verify:print` 会**自动**把它纳入对拍（那里遍历这张表）。
 */
export const SYMBOLOGIES: readonly SymbologySpec[] = [
  {
    id: 'code128',
    label: 'Code 128',
    kind: '1d',
    quietZone: 10,
    hint: '最通用的 1D 码：全 ASCII、长度可变。运单号 / 序列号默认用它',
    sample: 'ABC-123456',
  },
  {
    id: 'code39',
    label: 'Code 39',
    kind: '1d',
    quietZone: 10,
    hint: '老系统常见：只用大写字母与数字，密度低但容错好',
    sample: 'ABC-123',
  },
  {
    id: 'ean13',
    label: 'EAN-13',
    kind: '1d',
    quietZone: 10,
    hint: '商品条码：12 位数字（校验位自动算）或 13 位（校验位要对）',
    sample: '400638133393',
  },
  {
    id: 'qrcode',
    label: 'QR Code',
    kind: '2d',
    quietZone: 4,
    hint: '二维码：容量大、可纠错，手机扫码首选',
    sample: 'https://example.com/order/123',
    /* QR 的纠错等级是四档；等级越高越抗污损，但模块更多（同样的内容更"密"） */
    ecLevels: ['L', 'M', 'Q', 'H'],
  },
  {
    id: 'datamatrix',
    label: 'Data Matrix',
    kind: '2d',
    quietZone: 1,
    hint: '工业标记常用：小尺寸下比 QR 更抗损',
    sample: 'ABC-123456',
  },
]

export function symbologyOf(id: string): SymbologySpec | null {
  return SYMBOLOGIES.find((spec) => spec.id === id) ?? null
}

/* ══════════════════════════════════════════════════════════════════
   编码结果
   ══════════════════════════════════════════════════════════════════ */

/** 1D：条（x 是**内容区**里的模块坐标，静区另算） */
export interface BarSymbol {
  kind: '1d'
  /** 内容区宽度（模块） */
  contentModules: number
  quietZone: number
  /** 占地宽度（模块）= 静区 × 2 + 内容 */
  modules: number
  bars: { x: number; w: number }[]
}

/** 2D：模块矩阵（行主序、**从上到下**，坐标已翻正） */
export interface MatrixSymbol {
  kind: '2d'
  cols: number
  rows: number
  quietZone: number
  modules: number
  /** `rows * cols` 个布尔，`matrix[row * cols + col]` */
  matrix: boolean[]
}

export type EncodedSymbol = BarSymbol | MatrixSymbol

export type EncodeResult =
  | { ok: true; symbol: EncodedSymbol }
  | { ok: false; message: string }

/* ══════════════════════════════════════════════════════════════════
   收集绘图调用
   ══════════════════════════════════════════════════════════════════ */

/** bwip 的绘图上下文里我们用到的那一面（结构化类型，不引它的 .d.ts） */
interface DrawingContext {
  setopts(options: Record<string, unknown>): void
  scale(sx: number, sy: number): [number, number]
  measure(
    str: string,
    font: string,
    fwidth: number,
    fheight: number,
  ): { width: number; ascent: number; descent: number }
  init(width: number, height: number): void
  line(x0: number, y0: number, x1: number, y1: number, lw: number, rgb: string): void
  polygon(points: [number, number][]): void
  hexagon(points: unknown): void
  ellipse(x: number, y: number, rx: number, ry: number, ccw: boolean): void
  fill(rgb: string): void
  text(x: number, y: number, str: string, rgb: string, font: unknown): void
  end(): unknown
}

/** 编码函数（bwip 的具名导出形状；显式转成我们自己的类型，免得它的 .d.ts 漏进产物） */
type EncoderFn = (options: Record<string, unknown>, drawing: DrawingContext) => unknown

const ENCODERS: Record<string, EncoderFn> = {
  code128: code128 as unknown as EncoderFn,
  code39: code39 as unknown as EncoderFn,
  ean13: ean13 as unknown as EncoderFn,
  qrcode: qrcode as unknown as EncoderFn,
  datamatrix: datamatrix as unknown as EncoderFn,
}

interface Collected {
  bars: { x: number; w: number }[]
  polys: [number, number][][]
  width: number
  height: number
  /** 一个模块 = 多少个绘图单位（最后一次 `scale()` 的值，见文件头 ②） */
  unitsPerModule: number
}

/**
 * 跑一次编码，把绘图调用收成几何。
 *
 * ⚠️ 传进去的上下文是**我们自己的**：不收像素、不碰 DOM、不认字体
 * （`measure` 只做 HRI 排版用，而我们自己画 HRI，所以给个保守估算即可）。
 * 这样同一份代码在 V8 与 QuickJS 里结果逐位相同（架构文档 §5.3 的判据）。
 */
function collect(spec: SymbologySpec, text: string, options: EncodeOptions = {}): Collected {
  const encoder = ENCODERS[spec.id]
  if (!encoder) throw new Error(`没有 ${spec.id} 的编码器`)

  const out: Collected = { bars: [], polys: [], width: 0, height: 0, unitsPerModule: 1 }

  const drawing: DrawingContext = {
    setopts: () => {},
    scale: (sx: number, sy: number) => {
      /* ⚠️ 取**最后一次**：2D 会追加一次（QR 的模块在单位里是 2×scale） */
      out.unitsPerModule = Math.abs(sx) || 1
      return [sx, sy]
    },
    /*
     * 只给人眼看的 HRI 用得到；我们不用 bwip 的 HRI（架构文档 §5.5：
     * HRI 要和正文同一条绘制路径、不能用打印机内置字体），所以给个估算。
     */
    measure: (str: string, _font: string, fwidth: number, fheight: number) => ({
      width: fwidth * str.length,
      ascent: fheight * 0.8,
      descent: fheight * 0.2,
    }),
    init: (width: number, height: number) => {
      out.width = width
      out.height = height
    },
    /*
     * 1D 的每一根条。⚠️ `x0` 是**描边中心**（bwip 的绘图单位是像素对齐的），
     * 所以左边缘 = x0 - lw/2；因为 scale 是偶数，这个减法精确。
     */
    line: (x0: number, y0: number, x1: number, y1: number, lw: number) => {
      out.bars.push({ x: Math.min(x0, x1) - lw / 2, w: lw })
    },
    polygon: (points: [number, number][]) => out.polys.push(points),
    hexagon: () => {},
    ellipse: () => {},
    fill: () => {},
    text: () => {},
    end: () => null,
  }

  encoder(
    {
      bcid: spec.id,
      text,
      scale: ENCODER_SCALE,
      /*
       * ⚠️ 纠错等级要**显式传给编码器**，而且它改变的是**矩阵本身**
       * （等级越高模块越多）—— 所以它必须进组件值、也必须进缓存的键
       * （见 `evaluators.ts` 的编码缓存：键里少一个参数就是"改了没反应"）。
       */
      /*
       * ⚠️ **`eclevel` 必须和 `fixedeclevel: true` 一起传** —— 这是实测出来的坑，
       * 单独传 `eclevel` 它**什么都不做**：
       *
       * BWIPP 的 qrcode 默认会自动**往上升级**纠错等级（"用装得下的最高等级"），
       * 于是 L / M / Q / H 四个值对同一段内容给出**一模一样**的矩阵
       * （实测：格式位都是 `001100111`，也就是 H 的那一组 —— 短内容全都升到 H）。
       * 表现是"改了纠错等级，纸上的码纹丝不动"，而且换个长内容又好像有反应
       * （那时等级真的把版本顶上去了），最难查的那种。
       * 传 `fixedeclevel: true` 之后四档才各自成立（格式位、黑模块数都不同）。
       */
      ...(spec.ecLevels && options.ecLevel && spec.ecLevels.includes(options.ecLevel)
        ? { eclevel: options.ecLevel, fixedeclevel: true }
        : {}),
      /* 一维条码的高度固定成条高之外的值不影响模块几何（高度不参与 x 轴） */
    },
    drawing,
  )
  return out
}

/* ══════════════════════════════════════════════════════════════════
   多边形 → 模块矩阵（2D）
   ══════════════════════════════════════════════════════════════════ */

/**
 * 把 bwip 给的**直角多边形**填成模块矩阵。
 *
 * bwip 的 2D 符号是一串 `polygon()` 调用，最后 `fill()` —— 它的 SVG 后端
 * 用的是 `fill-rule="evenodd"`，而且注释里写明这些多边形**互不相交**。
 * 所以这里用**扫描线 + 奇偶规则**逐行求交，等价于它的填充。
 *
 * ⚠️ **y 轴方向是实测出来的，不要凭直觉翻**：bwip 的绘图坐标是
 * **y 向下**（`init(w,h)` 之后 y=0 是**顶边**）。第一版按"数学坐标系"
 * 翻了 y，结果整张矩阵上下镜像 —— 二维码**照样扫得出来**（有纠错和
 * 朝向识别），只是位置全错，正是最难发现的那类。
 * 现在 `verify:print` 拿权威 `pixs` 逐位对拍，翻不翻由数据说了算。
 */
function fillPolygons(
  polys: readonly (readonly [number, number][])[],
  cols: number,
  rows: number,
): boolean[] {
  const matrix = new Array<boolean>(cols * rows).fill(false)
  for (let row = 0; row < rows; row += 1) {
    /* 取这一行模块的**中线**做扫描线（避开正好落在格线上的顶点） */
    const y = row + 0.5
    const crossings: number[] = []
    for (const poly of polys) {
      for (let i = 0; i < poly.length; i += 1) {
        const [x1, y1] = poly[i]!
        const [x2, y2] = poly[(i + 1) % poly.length]!
        /* 只算**跨过**这条扫描线的边（半开区间，避免顶点被数两次） */
        if (y1 === y2) continue
        if ((y1 <= y && y2 > y) || (y2 <= y && y1 > y)) {
          crossings.push(x1 + ((y - y1) / (y2 - y1)) * (x2 - x1))
        }
      }
    }
    crossings.sort((a, b) => a - b)
    for (let i = 0; i + 1 < crossings.length; i += 2) {
      const from = Math.max(0, Math.round(crossings[i]!))
      const to = Math.min(cols, Math.round(crossings[i + 1]!))
      for (let col = from; col < to; col += 1) {
        matrix[row * cols + col] = true
      }
    }
  }
  return matrix
}

/* ══════════════════════════════════════════════════════════════════
   对外：encode()
   ══════════════════════════════════════════════════════════════════ */

/** BWIPP 的错误名 → 给人看的中文提示（认不出来就原样带出它的说明） */
const ERROR_HINTS: Record<string, string> = {
  ean13badLength: 'EAN-13 要 12 位数字（校验位自动算）或 13 位（校验位要对）',
  ean13badCharacter: 'EAN-13 只能填数字',
  ean13badCheckDigit: 'EAN-13 的校验位不对（最后一位要不填、要填对）',
  code39badCharacter: 'Code 39 只支持大写字母、数字和 `- . $ / + %` 空格',
}

function describeError(spec: SymbologySpec, error: unknown): string {
  const raw = error instanceof Error ? error.message : String(error)
  /* BWIPP 的消息长这样：`bwipp.ean13badLength#6878: EAN-13 must be 12 or 13 digits` */
  const body = raw.replace(/^bwip-js:\s*/, '')
  const [, name = '', detail = body] = /^bwipp\.([A-Za-z0-9_]+)#\d+:\s*([\s\S]*)$/.exec(body) ?? []
  const hint = ERROR_HINTS[name]
  return `${spec.label} 编码失败：${hint ?? (detail.trim() || body)}`
}

/**
 * 编码一段文本。**不抛异常**（除了"没这个符号"这种编码期就知道的错）——
 * 返回 `{ ok:false, message }`，让调用方决定怎么显示
 * （节点求值器把它抛成诊断，页面编辑器把它画成占位框）。
 */
export function encode(symbology: string, text: string, options: EncodeOptions = {}): EncodeResult {
  const spec = symbologyOf(symbology)
  if (!spec) return { ok: false, message: `不认识的条码符号：${symbology || '（空）'}` }
  const value = text ?? ''
  if (value.trim() === '') return { ok: false, message: `${spec.label} 的内容是空的` }

  let collected: Collected
  try {
    collected = collect(spec, value, options)
  } catch (error) {
    return { ok: false, message: describeError(spec, error) }
  }

  const unit = collected.unitsPerModule
  if (!Number.isInteger(unit) || unit <= 0) {
    return { ok: false, message: `编码器返回了非整数的模块单位（${unit}），这版不支持` }
  }
  /*
   * ⚠️ 这里是"几何必须落在模块网格上"的**硬校验**。
   * 少了它，一个取整偏移会静默变成"条宽偶尔差一个点"——
   * 而那种错**扫得出来**（纠错兜着），只有拿卡尺量或对拍才看得见。
   */
  const toModules = (value: number): number | null => {
    const modules = value / unit
    return Number.isInteger(modules) ? modules : null
  }

  const widthModules = toModules(collected.width)
  if (widthModules === null) {
    return { ok: false, message: `${spec.label} 的宽度不在模块网格上（内部错误）` }
  }

  if (spec.kind === '1d') {
    /*
     * ⚠️ 1D 的**高度不校验**：它和 x 轴不是同一套单位 ——
     * 宽度是模块数，而高度是**物理尺寸**（bwip 的单位 = 点，
     * 由 `height` 选项决定，默认 13mm）。要求它整除只会误报。
     */
    const bars: BarSymbol['bars'] = []
    for (const bar of collected.bars) {
      const x = toModules(bar.x)
      const w = toModules(bar.w)
      if (x === null || w === null || w <= 0) {
        return { ok: false, message: `${spec.label} 的条宽不在模块网格上（内部错误）` }
      }
      bars.push({ x, w })
    }
    if (bars.length === 0) return { ok: false, message: `${spec.label} 没有编码出任何条` }
    /*
     * ⚠️ 这里**不记条高**：实测所有符号的条都是整高（EAN 的护线也一样，
     * `bhs` 全是 1），而条高在我们的模型里由组件的 `heightMm` 决定 ——
     * 记一个恒为 1 的比例只会误导（`init.height` 还比条高多 1 个单位的留白）。
     */
    return {
      ok: true,
      symbol: {
        kind: '1d',
        contentModules: widthModules,
        quietZone: spec.quietZone,
        modules: widthModules + spec.quietZone * 2,
        bars,
      },
    }
  }

  const heightModules = toModules(collected.height)
  if (heightModules === null) {
    return { ok: false, message: `${spec.label} 的高度不在模块网格上（内部错误）` }
  }

  const matrix = fillPolygons(
    collected.polys.map((poly) => poly.map(([x, y]) => [x / unit, y / unit] as [number, number])),
    widthModules,
    heightModules,
  )
  return {
    ok: true,
    symbol: {
      kind: '2d',
      cols: widthModules,
      rows: heightModules,
      quietZone: spec.quietZone,
      modules: widthModules + spec.quietZone * 2,
      matrix,
    },
  }
}

/* ══════════════════════════════════════════════════════════════════
   布局：模块 → 整数点 → mm
   ══════════════════════════════════════════════════════════════════ */

/**
 * **一个模块占几个设备点**（整数）。
 *
 * 这就是架构文档 §5.1 那条「整数点吸附」：模块宽必须是整数点，
 * 否则热敏头会把浮点宽度量化成忽宽忽窄的条，扫码等级掉一档。
 * 换算走 `units.ts` 那**一个**函数。
 */
export function moduleDotsFor(xDimMicron: number, dpi: number): number {
  return micronToDots(xDimMicron, dpi)
}

/** 实际生效的 X 维度（mm）—— 取整之后的真实值，和用户填的那个不是一回事 */
export function effectiveXDimMm(moduleDots: number, dpi: number): number {
  return (moduleDots * MM_PER_INCH) / dpi
}

/** 符号占地宽度（mm）：模块数 × 模块宽 */
export function symbolWidthMm(symbol: EncodedSymbol, moduleDots: number, dpi: number): number {
  return (symbol.modules * moduleDots * MM_PER_INCH) / dpi
}

/** 符号占地高度（mm）：1D 是条高、2D 是正方形 */
export function symbolHeightMm(
  symbol: EncodedSymbol,
  moduleDots: number,
  dpi: number,
  barHeightMm: number,
): number {
  if (symbol.kind === '1d') return barHeightMm
  return (symbol.rows * moduleDots * MM_PER_INCH) / dpi
}

/**
 * **规范对模块尺寸的容差**：5–10%（架构文档 §5.1 引的典型规范要求）。
 * 取严的一头（10%）作为"该提醒了"的分界线。
 */
export const XDIM_TOLERANCE = 0.1

/**
 * **X 维度合规检查** —— 返回一句人能读的警告，合格时 `null`。
 *
 * 两条**互相独立**的规矩，都要查（只查一条会漏掉一半的真问题）：
 *
 * | 规矩 | 判据 | 违反的后果 |
 * |---|---|---|
 * | **绝对值下限** | 生效的模块宽 ≥ 0.264mm（GS1，见 `units.ts`） | 条太窄 → 扫不出来 |
 * | **标称值容差** | 生效值相对标称值的偏差 ≤ 10% | 条宽不合规 → 等级下降 |
 *
 * ⚠️ 第二条是**整数点吸附的必然副产品**，也是这一层最容易被忽略的东西：
 * 名义上的 330µm 在 203dpi 下只能落到 3 个点（0.375mm，**+13.7%**），
 * 在 300dpi 下落到 4 个点（0.339mm，+2.6%）—— 同一个模板换个分辨率，
 * 条宽就变了 10%，而这一点**在设计器里完全看不出来**。
 * 反过来，挑一个"正好落在整数点上"的标称值（203dpi 的 375µm = 3 点）
 * 偏差可以小到 0.06%。
 */
export function xDimWarning(
  xDimMicron: number,
  dpi: number,
  options: { forGtin?: boolean } = {},
): string | null {
  const dots = moduleDotsFor(xDimMicron, dpi)
  const nominalMm = xDimMicron / 1000
  const actual = effectiveXDimMm(dots, dpi)

  /* ① 绝对下限（GS1 的 0.264mm） */
  const need = minGs1ModuleDots(dpi)
  if (actual < GS1_MIN_X_MM) {
    return (
      `X 维度只有 ${dots} 个点（${actual.toFixed(3)}mm）—— ${dpi}dpi 下至少要 ` +
      `${need} 个点（0.264mm，GS1 下限）。把 X 维度填到 ${Math.ceil(GS1_MIN_X_MM * 1000 / 10) * 10}µm 以上` +
      `（本机分辨率下正好落成整数点的是 ${Math.round((need * MM_PER_INCH * 1000) / dpi)}µm）。` +
      `${options.forGtin ? '（商品码尤其严）' : ''}`
    )
  }

  /* ② 标称值容差（取整带来的偏差） */
  const deviation = Math.abs(actual - nominalMm) / nominalMm
  if (deviation > XDIM_TOLERANCE) {
    const direction = actual > nominalMm ? '宽' : '窄'
    const exact = Math.round((dots * MM_PER_INCH * 1000) / dpi)
    return (
      `标称 ${xDimMicron}µm 在 ${dpi}dpi 下吸附成 ${dots} 个点 = ${actual.toFixed(3)}mm，` +
      `比标称${direction} ${(deviation * 100).toFixed(1)}%（规范容差 5–10%）。` +
      `要正好落成整数点，填 ${exact}µm 或 ${Math.round(((dots + 1) * MM_PER_INCH * 1000) / dpi)}µm。`
    )
  }
  return null
}

/**
 * **HRI（人眼可读文本）那一行的高度**（mm）。
 *
 * 为什么由我们定、而不是用 bwip 的 HRI：架构文档 §5.5 —— HRI **不能用
 * 打印机内置字体**，否则"预览居中、打印偏左"（固件字体的字宽和我们的
 * 字体不是一个东西）。所以 HRI 走我们自己的文本绘制路径（编辑器里是
 * DOM 文本、IR 里是 `text` op），高度也得我们自己算。
 *
 * 取值：条高的 26%，夹在 1.5–4mm —— 低于 1.5mm 在高 dpi 下糊成一团，
 * 高于 4mm 会喧宾夺主。
 */
/**
 * HRI 那一行该多高（mm）。
 *
 * 显式给字号就用它（夹到 0.5mm 以上 —— 再小就打不出字了）；
 * 给 0 表示**自动**：条高的 26%，夹在 1.5–4mm。
 *
 * ⚠️ 自动那条规矩（26% / 夹取）只许有这一处实现：HRI 的高度同时影响
 * 组件框的高度、IR 里的 text op 字号、以及"条码能不能扫"的观感 ——
 * 三个地方各算一次的话，打印出来会发现框和字不一样高。
 */
export function hriHeightMm(barHeightMm: number, explicitMm = 0): number {
  if (explicitMm > 0) return Math.max(0.5, explicitMm)
  return Math.min(4, Math.max(1.5, barHeightMm * 0.26))
}

/** 编码失败时占位框的宽度（mm）—— 让"这里有东西但编码错了"看得见、点得中 */
export const BARCODE_PLACEHOLDER_MM = 30
